import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// LOT 11F — SEO public. Aucun domaine provisoire n'est déclaré canonique.
// Configurer REACT_APP_PUBLIC_SITE_URL uniquement après choix du domaine définitif.
const BRAND = "ProScore Live";
const DEFAULT = {
  title: "ProScore Live — Analyses et statistiques football",
  description: "Scores, compétitions, forme des équipes, briefings et projections statistiques expliquées pour suivre le football.",
};
const PAGES = {
  "/": DEFAULT,
  "/matchs": {
    title: "Matchs de football et analyses | ProScore Live",
    description: "Consultez les rencontres, scores, statistiques et analyses disponibles pour les matchs de football.",
  },
  "/competitions": {
    title: "Compétitions et classements football | ProScore Live",
    description: "Explorez les compétitions de football, leurs rencontres et leurs classements disponibles.",
  },
  "/coupon": {
    title: "Sélection Pro : analyses football | ProScore Live",
    description: "Découvrez la Sélection Pro du jour et personnalisez votre sélection à partir des analyses publiées.",
  },
  "/offres": {
    title: "Offres et accès aux analyses | ProScore Live",
    description: "Comparez les formules d'accès aux analyses football et aux fonctionnalités de ProScore Live.",
  },
  "/assistance": {
    title: "Assistance et contact | ProScore Live",
    description: "Consultez les informations d'assistance disponibles pour utiliser ProScore Live et gérer votre compte.",
  },
  "/confidentialite": {
    title: "Politique de confidentialité | ProScore Live",
    description: "Découvrez quelles données sont utilisées par ProScore Live et comment elles participent au fonctionnement du service.",
  },
  "/conditions-utilisation": {
    title: "Conditions d'utilisation | ProScore Live",
    description: "Consultez les conditions d'utilisation et les limites des analyses football proposées par ProScore Live.",
  },
  "/mentions-legales": {
    title: "Mentions légales | ProScore Live",
    description: "Retrouvez les informations légales relatives au service ProScore Live.",
  },
  "/cookies": {
    title: "Cookies et stockage local | ProScore Live",
    description: "Comprenez l'utilisation des cookies et du stockage du navigateur lors de votre navigation sur ProScore Live.",
  },
};
const PRIVATE = {
  title: "Espace privé | ProScore Live",
  description: "Espace personnel réservé aux utilisateurs de ProScore Live.",
};

function setMeta(attribute, name, content) {
  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!content) {
    if (element) element.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!url) {
    if (element) element.remove();
    return;
  }
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", url);
}

function cleanPath(path) {
  const noSlash = (path || "/").replace(/\/+$/, "") || "/";
  return noSlash === "/matches" ? "/matchs" : noSlash;
}

function pageFor(path) {
  if (PAGES[path]) return { ...PAGES[path], publicPage: true };
  if (path === "/compte" || path === "/admin" || path.startsWith("/admin/")) {
    return { ...PRIVATE, publicPage: false };
  }
  if (/^\/matchs\/[^/]+$/.test(path)) {
    return {
      title: "Fiche match : statistiques et analyses | ProScore Live",
      description: "Consultez la fiche d'une rencontre, les statistiques et les analyses disponibles avant ou après le match.",
      publicPage: true,
    };
  }
  if (/^\/competitions\/[^/]+$/.test(path)) {
    return {
      title: "Classement et rencontres d'une compétition | ProScore Live",
      description: "Retrouvez les classements, rencontres et statistiques disponibles pour cette compétition de football.",
      publicPage: true,
    };
  }
  return { title: "Page introuvable | ProScore Live", description: "", publicPage: false };
}

function productionBase() {
  const configured = (process.env.REACT_APP_PUBLIC_SITE_URL || "").trim().replace(/\/+$/, "");
  if (!configured) return "";
  try {
    const url = new URL(configured);
    if (url.protocol !== "https:" || url.origin !== window.location.origin) return "";
    return url.origin;
  } catch (_) {
    return "";
  }
}

export default function SeoHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = cleanPath(pathname);
    const page = pageFor(path);
    const site = productionBase();
    const indexable = Boolean(site && page.publicPage);
    const canonical = indexable ? `${site}${path === "/" ? "/" : path}` : "";

    document.title = page.title;
    document.documentElement.lang = "fr";
    setMeta("name", "description", page.description);
    setMeta("name", "robots", indexable ? "index, follow" : "noindex, nofollow");
    setMeta("property", "og:title", page.title);
    setMeta("property", "og:description", page.description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", BRAND);
    setMeta("property", "og:locale", "fr_FR");
    setMeta("name", "twitter:card", "summary");
    setMeta("name", "twitter:title", page.title);
    setMeta("name", "twitter:description", page.description);
    setMeta("property", "og:url", canonical);
    setCanonical(canonical);

    let structured = document.getElementById("ps-public-website-jsonld");
    if (structured) structured.remove();
    if (indexable && path === "/") {
      structured = document.createElement("script");
      structured.id = "ps-public-website-jsonld";
      structured.type = "application/ld+json";
      structured.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: BRAND,
        url: `${site}/`,
        inLanguage: "fr",
        description: DEFAULT.description,
      });
      document.head.appendChild(structured);
    }
  }, [pathname]);
  return null;
}
